import { z } from "zod";

export const eInvoiceSchema = z
  .object({
    registrationName: z
      .string()
      .trim()
      .min(1, "Please enter the registration name.")
      .max(50, "Registration name must not exceed 50 characters."),

    tinNo: z
      .string()
      .trim()
      .min(1, "Please enter the TIN no.")
      .max(20, "TIN no. must not exceed 20 characters."),

    sstNo: z
      .any()
      .transform((value) => (value == null ? "" : String(value)).trim())
      .pipe(z.string().max(20, "SST no. must not exceed 20 characters.")),

    idType: z
      .string()
      .min(1, "Please select an ID type."),

    nricBrnNo: z
      .string()
      .trim()
      .min(1, "Please enter the NRIC / BRN no.")
      .max(20, "NRIC / BRN no. must not exceed 20 characters."),

    msicCode: z
      .any()
      .transform((value) => (value == null ? "" : String(value)).trim())
      .pipe(
        z.string().refine(
          (value) => value === "" || /^\d{5}$/.test(value),
          "MSIC code must be exactly 5 digits.",
        ),
      ),

    contactNoCountryCode: z.string().min(1, "Please select a country code."),

    contactNo: z
      .string()
      .min(7, "Please enter the valid contact number.")
      .max(15, "Contact number must not exceed 15 characters.")
      .regex(/^[0-9]+$/, "Please enter a valid contact number."),

    email: z
      .string()
      .trim()
      .min(1, "Please enter an email address.")
      .email("Please enter a valid email address.")
      .max(100, "Email must not exceed 100 characters."),

    address1: z
      .string()
      .trim()
      .min(1, "Please enter address line 1.")
      .max(200, "Address line 1 must not exceed 200 characters."),

    address2: z
      .any()
      .transform((value) => (value == null ? "" : String(value)).trim())
      .pipe(
        z.string().max(200, "Address line 2 must not exceed 200 characters."),
      ),

    city: z
      .string()
      .min(1, "Please select a city."),

    postcode: z
      .string()
      .trim()
      .regex(/^\d{5}$/, "Please enter a valid postcode (eg: 00000)."),

    state: z
      .string()
      .min(1, "Please select the state."),

    country: z
      .string()
      .min(1, "Please select the country."),

    stateId: z
      .string()
      .min(1, "State ID is required."),

    countryId: z
      .string()
      .min(1, "Country ID is required."),

    cityId: z
      .string()
      .min(1, "City ID is required."),

    businessActivities: z
      .string()
      .trim()
      .min(1, "Please enter the business activities.")
      .max(500, "Business activities must not exceed 500 characters."),
  })
  .superRefine((data, ctx) => {
    if (data.contactNo.trim().length > 0 && !data.contactNoCountryCode.trim()) {
      ctx.addIssue({
        code: "custom",
        path: ["contactNoCountryCode"],
        message: "Please select a country code.",
      });
    }

    if (data.contactNoCountryCode === "+60") {
      const phone = data.contactNo;

      // Malaysia mobile number must be exactly 9 or 10 digits
      if (phone.length !== 9 && phone.length !== 10) {
        ctx.addIssue({
          code: "custom",
          path: ["contactNo"],
          message: "Enter a valid Malaysian mobile number (e.g. 123456789)",
        });
      }

      // Cannot start with 0
      if (phone.startsWith("0")) {
        ctx.addIssue({
          code: "custom",
          path: ["contactNo"],
          message: "Enter a valid Malaysian mobile number (e.g. 123456789)",
        });
      }
    }
  });

export type EInvoiceValues = z.infer<typeof eInvoiceSchema>;